import React, { useCallback, useEffect, useMemo, useRef, useState, } from "react";
export default function VirtualList({ items, renderItem, height, estimatedItemHeight = 60, overscan = 5, className, style, _messagesListEndRef, stickyToBottom, scrollUp, id }) {
    // measured heights map
    const [heights, setHeights] = useState(() => new Map());
    // refs
    const containerRef = useRef(null);
    const itemsRef = useRef(new Map());
    const resizeObsRef = useRef(null);
    // scroll position
    const [scrollTop, setScrollTop] = useState(0);
    // Build prefix sums (cumulative heights) from measured heights + estimates
    const { totalHeight, getOffsetForIndex } = useMemo(() => {
        const n = items.length;
        const sizes = new Array(n);
        for (let i = 0; i < n; i++) {
            const h = heights.get(i);
            sizes[i] = h == null ? estimatedItemHeight : h;
        }
        const prefix = new Array(n + 1);
        prefix[0] = 0;
        for (let i = 0; i < n; i++)
            prefix[i + 1] = prefix[i] + sizes[i];
        return {
            totalHeight: prefix[n],
            getOffsetForIndex: (index) => prefix[index],
        };
    }, [items.length, heights, estimatedItemHeight]);
    // compute visible window by binary searching prefix sums (linear search is OK for moderate N)
    const visibleRange = useMemo(() => {
        const startOffset = scrollTop;
        const endOffset = scrollTop + height;
        // find first index whose offset + size > startOffset
        let start = 0;
        let sum = 0;
        while (start < items.length) {
            const h = heights.get(start) ?? estimatedItemHeight;
            if (sum + h > startOffset)
                break;
            sum += h;
            start++;
        }
        // expand until covers endOffset
        let end = start;
        let acc = sum;
        while (end < items.length && acc < endOffset) {
            acc += heights.get(end) ?? estimatedItemHeight;
            end++;
        }
        // apply overscan
        start = Math.max(0, start - overscan);
        end = Math.min(items.length, end + overscan);
        return { start, end };
    }, [scrollTop, height, items.length, heights, estimatedItemHeight, overscan]);
    // scroll handler (rAF for smoothness)
    let lastScrollTop = 0;
    useEffect(() => {
        const el = containerRef.current;
        if (!el)
            return;
        let raf = 0;
        const onScroll = () => {
            if (raf)
                cancelAnimationFrame(raf);
            raf = requestAnimationFrame(() => {
                setScrollTop(el.scrollTop);
            });
            const current = el.scrollTop;
            if (current < lastScrollTop) {
                console.log("⬆️ Scrolling UP");
                // 👉 You can trigger your logic here (e.g. show header)
                if (scrollUp) {
                    scrollUp();
                }
            }
            else if (current > lastScrollTop) {
                console.log("⬇️ Scrolling DOWN");
                // 👉 Or do something else
            }
            lastScrollTop = current <= 0 ? 0 : current;
        };
        el.addEventListener("scroll", onScroll, { passive: true });
        return () => {
            if (raf)
                cancelAnimationFrame(raf);
            el.removeEventListener("scroll", onScroll);
        };
    }, []);
    // Safe ResizeObserver setup: observe only visible items and debounce updates
    useEffect(() => {
        if (typeof ResizeObserver === "undefined")
            return;
        const obs = new ResizeObserver((entries) => {
            // debounce via microtask + setTimeout 0 to avoid "ResizeObserver loop completed" in some browsers
            // collect changes
            const changed = [];
            for (const entry of entries) {
                const target = entry.target;
                const idxAttr = target.dataset.vindex;
                if (!idxAttr)
                    continue;
                const idx = Number(idxAttr);
                const measured = Math.round(target.getBoundingClientRect().height);
                changed.push([idx, measured]);
            }
            if (changed.length === 0)
                return;
            // schedule update
            Promise.resolve().then(() => {
                setTimeout(() => {
                    setHeights((prev) => {
                        let did = false;
                        const next = new Map(prev);
                        for (const [i, h] of changed) {
                            if (next.get(i) !== h) {
                                next.set(i, h);
                                did = true;
                            }
                        }
                        return did ? next : prev;
                    });
                }, 0);
            });
        });
        resizeObsRef.current = obs;
        return () => {
            obs.disconnect();
            resizeObsRef.current = null;
        };
    }, []);
    // attach/detach observers to only currently rendered items
    useEffect(() => {
        const obs = resizeObsRef.current;
        if (!obs)
            return;
        // unobserve everything first to be safe
        obs.disconnect();
        // observe visible items' elements
        for (let i = visibleRange.start; i < visibleRange.end; i++) {
            const el = itemsRef.current.get(i);
            if (el)
                obs.observe(el);
        }
        // cleanup: nothing here since next effect run will disconnect or re-observe
    }, [visibleRange.start, visibleRange.end]);
    // when item elements mount/update, measure initial heights (so prefix sums are more accurate before RO fires)
    const measureItemNow = useCallback((index) => {
        const el = itemsRef.current.get(index);
        if (!el)
            return;
        const measured = Math.round(el.getBoundingClientRect().height);
        setHeights((prev) => {
            const current = prev.get(index);
            if (current === measured)
                return prev;
            const next = new Map(prev);
            next.set(index, measured);
            return next;
        });
    }, []);
    // item renderer wrapper to attach ref & data attribute
    const ItemWrapper = useCallback(({ index, children }) => {
        const refCallback = (el) => {
            if (el) {
                itemsRef.current.set(index, el);
                // set dataset for RO callback
                el.dataset.vindex = String(index);
                // measure immediate
                measureItemNow(index);
            }
            else {
                itemsRef.current.delete(index);
            }
        };
        const style = {
        // no enforced height: allow natural content size
        };
        return (React.createElement("div", { ref: refCallback, style: style, "data-vindex": index }, children));
    }, [measureItemNow]);
    // build list of visible children
    const children = [];
    for (let i = visibleRange.start; i < visibleRange.end; i++) {
        children.push(React.createElement(ItemWrapper, { key: i, index: i }, renderItem(items[i], i)));
    }
    // compute padding (top offset) for first rendered item
    const top = getOffsetForIndex(visibleRange.start);
    const viewportStyle = {
        height,
        overflow: "auto",
        position: "relative",
        willChange: "transform",
        WebkitOverflowScrolling: "touch",
        ...style,
    };
    const innerStyle = {
        height: totalHeight,
        position: "relative",
    };
    const contentStyle = {
        position: "absolute",
        top,
        left: 0,
        right: 0,
    };
    const [isAtBottom, setIsAtBottom] = useState(true);
    return (React.createElement("div", { id: id, ref: containerRef, className: className, style: viewportStyle, role: "list", "aria-label": "virtual-list" },
        React.createElement("div", { style: innerStyle },
            React.createElement("div", { style: contentStyle },
                children,
                React.createElement("div", { id: 'messagesListEnd', ref: _messagesListEndRef })))));
}
